import React from 'react';
import { MapPin, Clock, Utensils, ShoppingBag, Landmark, Bike, Package, Store, Church, Building2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
export function KochiGuide() {
  const nearbyDining = [{
    name: 'Empire Plaza Restaurant',
    distance: '~850 m',
    walkTime: '7 mins walk',
    description: 'Offers a mix of cuisines including Kerala, North Indian, and some Chinese options.'
  }, {
    name: 'Ramees Restaurant',
    distance: '~750 m',
    walkTime: '6 mins walk',
    description: 'Very popular nearby spot for Malabar Biryani and a variety of other dishes.'
  }, {
    name: 'New Usha Bakery',
    distance: '~1.0 km',
    walkTime: '13 mins walk',
    description: 'Local bakery for fresh breads, pastries, cakes, snacks, and traditional sweets.'
  }, {
    name: 'Milano Ice Cream',
    distance: 'Inside Forum Mall, ~1.0 km',
    walkTime: '8 mins walk',
    description: 'Offers a good variety of ice cream flavors and sundaes inside the nearby mall.'
  }];
  const culturalSpots = [{
    name: 'Kerala Folklore Museum',
    distance: '~3.2 km',
    driveTime: '6 mins',
    description: "A must-visit private museum showcasing Kerala's rich heritage, with stunning architecture, cultural artifacts, and traditional art forms."
  }, {
    name: 'Sree Poornathrayeesa Temple',
    distance: '~4.9 km',
    driveTime: '10 mins',
    description: 'A grand and ancient temple in Tripunithura, dedicated to Lord Vishnu. Renowned for its unique architecture and as a center for traditional temple arts.'
  }, {
    name: 'Hill Palace Museum',
    distance: '~8.2 km',
    driveTime: '15 mins',
    description: 'A sprawling museum complex showcasing the royal heritage and architecture of Kerala.'
  }, {
    name: 'Marine Drive, Kochi',
    distance: '~9.3 km',
    driveTime: '17 mins',
    description: 'Perfect for evening walks with scenic backwater views and local street food.'
  }, {
    name: 'Mattancherry Palace (Dutch Palace)',
    distance: '~10.8 km',
    driveTime: '18 mins',
    description: "A glimpse into Kerala's royal history with stunning mural paintings and artifacts."
  }, {
    name: 'Santa Cruz Basilica',
    distance: '~13 km',
    driveTime: '21 mins',
    description: "One of India's oldest and most beautiful churches, known for its Indo-European design."
  }, {
    name: 'Fort Kochi',
    distance: '~13.3 km',
    driveTime: '22 mins',
    description: 'Heritage streets, cafés, and the iconic Chinese fishing nets by the waterfront.'
  }];
  const modernMalls = [{
    name: 'Forum Mall',
    distance: '~1 km',
    driveTime: '4 mins',
    description: 'The closest modern mall to you. Features a large Lulu Hypermarket (great for groceries/spices), food court, retail shops, and Fun City for kids.'
  }, {
    name: 'Lulu Edapally Shopping Mall',
    distance: '~11 km',
    driveTime: '18 mins',
    description: "A massive destination mall with a hypermarket, international food court, luxury brands, and Funtura—one of the city's largest kids' play zones."
  }];
  const traditionalMarkets = [{
    name: 'Kairali Handicrafts Emporium (MG Road)',
    distance: '~7.4 km',
    driveTime: '13 mins',
    description: 'A Kerala Government-run emporium. Best for authentic, high-quality local handicrafts like Kathakali masks, wooden elephants, and brass lamps.'
  }, {
    name: 'Broadway, Ernakulam',
    distance: '~10.3 km',
    driveTime: '18 mins',
    description: "The bustling heart of Kochi's local commerce. A narrow street packed with shops selling spices, textiles, electronics, and wholesale goods."
  }, {
    name: 'Jew Town (Mattancherry)',
    distance: '~10.5 km',
    driveTime: '18 mins',
    description: 'The traditional place to buy spices, lined with historic warehouses. Also the best spot for antiques and unique souvenirs.'
  }];
  return <div className="min-h-screen bg-white" data-id="element-327">
      {/* Hero - Smaller Banner */}
      <div className="relative h-[30vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#2C2C2C] via-[#3D3D3D] to-[#1A1A1A]" data-id="element-328">
        <div className="absolute inset-0 opacity-10" data-id="element-329">
          <div className="absolute top-10 left-10 w-48 h-48 bg-[#D4AF37] rounded-full blur-3xl" data-id="element-330"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-[#8B7355] rounded-full blur-3xl" data-id="element-331"></div>
        </div>
        <div className="relative z-10 text-center px-4 max-w-4xl" data-id="element-332">
          <span className="inline-block px-4 py-1 bg-[#D4AF37] text-white text-xs md:text-sm font-bold rounded-full mb-3 uppercase tracking-wider" data-id="element-333">
            Explorer's Handbook
          </span>
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mb-2" data-id="element-334">
            Experience Kochi with Oshara Collective
          </h1>
          <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto" data-id="element-335">
            Serene Stays at The Ark & The Haven
          </p>
        </div>
      </div>

      {/* Culture & Heritage - Moved to Top */}
      <section className="py-20 bg-white" data-id="element-336">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-337">
          <div className="flex items-center gap-3 mb-8" data-id="element-338">
            <Landmark className="h-8 w-8 text-[#8B7355]" data-id="element-339" />
            <div data-id="element-340">
              <h2 className="font-serif text-3xl font-bold text-[#2C2C2C]" data-id="element-341">
                Culture, Heritage & Sightseeing
              </h2>
              <p className="text-[#5C5C5C]" data-id="element-342">
                Step outside to experience Kochi's charm, serene backwaters, and
                historic quarters.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-343">
            {culturalSpots.map((spot, idx) => <Card key={idx} className="p-6 hover:shadow-xl transition-shadow duration-300 bg-[#FAF7F2]" data-id="element-344">
                <div className="flex items-start gap-3 mb-3" data-id="element-345">
                  <div className="bg-[#8B7355]/10 p-2 rounded-lg shrink-0" data-id="element-346">
                    {idx < 3 ? <Landmark className="h-5 w-5 text-[#8B7355]" data-id="element-347" /> : idx < 5 ? <Building2 className="h-5 w-5 text-[#8B7355]" data-id="element-348" /> : <Church className="h-5 w-5 text-[#8B7355]" data-id="element-349" />}
                  </div>
                  <div className="flex-1" data-id="element-350">
                    <h3 className="font-serif text-lg font-bold text-[#2C2C2C] mb-1" data-id="element-351">
                      {spot.name}
                    </h3>
                    <div className="flex items-center gap-2 text-xs text-[#5C5C5C] mb-2" data-id="element-352">
                      <span className="flex items-center gap-1" data-id="element-353">
                        <MapPin className="h-3 w-3" data-id="element-354" />
                        {spot.distance}
                      </span>
                      <span data-id="element-355">•</span>
                      <span className="flex items-center gap-1" data-id="element-356">
                        <Clock className="h-3 w-3" data-id="element-357" />
                        {spot.driveTime}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-[#5C5C5C] leading-relaxed" data-id="element-358">
                  {spot.description}
                </p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Nearby Dining Section */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-359">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-360">
          <div className="flex items-center gap-3 mb-8" data-id="element-361">
            <Utensils className="h-8 w-8 text-[#D4AF37]" data-id="element-362" />
            <div data-id="element-363">
              <h2 className="font-serif text-3xl font-bold text-[#2C2C2C]" data-id="element-364">
                Nearby Dining & Treats
              </h2>
              <p className="text-[#5C5C5C]" data-id="element-365">
                Craving a quick bite? These spots are just a short stroll away.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-366">
            {nearbyDining.map((spot, idx) => <Card key={idx} className="p-6 hover:shadow-lg transition-shadow duration-300 bg-white" data-id="element-367">
                <div className="flex items-start gap-4" data-id="element-368">
                  <div className="bg-[#D4AF37]/10 p-3 rounded-lg shrink-0" data-id="element-369">
                    <Utensils className="h-6 w-6 text-[#D4AF37]" data-id="element-370" />
                  </div>
                  <div className="flex-1" data-id="element-371">
                    <h3 className="font-serif text-xl font-bold text-[#2C2C2C] mb-2" data-id="element-372">
                      {spot.name}
                    </h3>
                    <div className="flex items-center gap-3 text-sm text-[#5C5C5C] mb-3" data-id="element-373">
                      <span className="flex items-center gap-1" data-id="element-374">
                        <MapPin className="h-4 w-4" data-id="element-375" />
                        {spot.distance}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-376">
                        <Clock className="h-4 w-4" data-id="element-377" />
                        {spot.walkTime}
                      </span>
                    </div>
                    <p className="text-sm text-[#5C5C5C] leading-relaxed" data-id="element-378">
                      {spot.description}
                    </p>
                  </div>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* Delivery & Essentials */}
      <section className="py-20 bg-white" data-id="element-379">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-380">
          <div className="flex items-center gap-3 mb-8" data-id="element-381">
            <Bike className="h-8 w-8 text-[#8B7355]" data-id="element-382" />
            <div data-id="element-383">
              <h2 className="font-serif text-3xl font-bold text-[#2C2C2C]" data-id="element-384">
                Delivery & Essentials
              </h2>
              <p className="text-[#5C5C5C]" data-id="element-385">
                Prefer to stay in? We've got you covered.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-386">
            <Card className="p-8 bg-[#FAF7F2]" data-id="element-387">
              <div className="flex items-center gap-3 mb-4" data-id="element-388">
                <div className="bg-[#FF385C]/10 p-3 rounded-lg" data-id="element-389">
                  <Utensils className="h-6 w-6 text-[#FF385C]" data-id="element-390" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#2C2C2C]" data-id="element-391">
                  Food Delivery
                </h3>
              </div>
              <p className="text-[#5C5C5C] leading-relaxed" data-id="element-392">
                Popular apps like{' '}
                <span className="font-bold text-[#2C2C2C]" data-id="element-393">Zomato</span> and{' '}
                <span className="font-bold text-[#2C2C2C]" data-id="element-394">Swiggy</span> cover
                this area efficiently for doorstep delivery.
              </p>
            </Card>

            <Card className="p-8 bg-[#FAF7F2]" data-id="element-395">
              <div className="flex items-center gap-3 mb-4" data-id="element-396">
                <div className="bg-[#25D366]/10 p-3 rounded-lg" data-id="element-397">
                  <Package className="h-6 w-6 text-[#25D366]" data-id="element-398" />
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#2C2C2C]" data-id="element-399">
                  Groceries & Household
                </h3>
              </div>
              <p className="text-[#5C5C5C] leading-relaxed" data-id="element-400">
                For groceries, household items, or quick essentials, delivery
                services like{' '}
                <span className="font-bold text-[#2C2C2C]" data-id="element-401">Big Basket</span>,{' '}
                <span className="font-bold text-[#2C2C2C]" data-id="element-402">
                  Swiggy Instamart
                </span>
                , and <span className="font-bold text-[#2C2C2C]" data-id="element-403">Blinkit</span>{' '}
                are readily available and deliver right to The Ark Maradu.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Shopping Section */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-404">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-405">
          <div className="flex items-center gap-3 mb-8" data-id="element-406">
            <ShoppingBag className="h-8 w-8 text-[#D4AF37]" data-id="element-407" />
            <div data-id="element-408">
              <h2 className="font-serif text-3xl font-bold text-[#2C2C2C]" data-id="element-409">
                Shopping & Souvenirs
              </h2>
              <p className="text-[#5C5C5C]" data-id="element-410">
                From modern malls to traditional spice markets.
              </p>
            </div>
          </div>

          {/* Modern Malls */}
          <div className="mb-12" data-id="element-411">
            <h3 className="font-serif text-2xl font-bold text-[#2C2C2C] mb-6 flex items-center gap-2" data-id="element-412">
              <Building2 className="h-6 w-6 text-[#8B7355]" data-id="element-413" />
              Modern Malls
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-414">
              {modernMalls.map((mall, idx) => <Card key={idx} className="p-6 bg-white hover:shadow-lg transition-shadow duration-300" data-id="element-415">
                  <div className="flex items-start gap-4" data-id="element-416">
                    <div className="bg-[#8B7355]/10 p-3 rounded-lg shrink-0" data-id="element-417">
                      <ShoppingBag className="h-6 w-6 text-[#8B7355]" data-id="element-418" />
                    </div>
                    <div className="flex-1" data-id="element-419">
                      <h4 className="font-serif text-xl font-bold text-[#2C2C2C] mb-2" data-id="element-420">
                        {mall.name}
                      </h4>
                      <div className="flex items-center gap-3 text-sm text-[#5C5C5C] mb-3" data-id="element-421">
                        <span className="flex items-center gap-1" data-id="element-422">
                          <MapPin className="h-4 w-4" data-id="element-423" />
                          {mall.distance}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-424">
                          <Clock className="h-4 w-4" data-id="element-425" />
                          {mall.driveTime}
                        </span>
                      </div>
                      <p className="text-sm text-[#5C5C5C] leading-relaxed" data-id="element-426">
                        {mall.description}
                      </p>
                    </div>
                  </div>
                </Card>)}
            </div>
          </div>

          {/* Traditional Markets */}
          <div data-id="element-427">
            <h3 className="font-serif text-2xl font-bold text-[#2C2C2C] mb-6 flex items-center gap-2" data-id="element-428">
              <Store className="h-6 w-6 text-[#8B7355]" data-id="element-429" />
              Traditional Markets & Handicrafts
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-430">
              {traditionalMarkets.map((market, idx) => <Card key={idx} className="p-6 bg-white hover:shadow-lg transition-shadow duration-300" data-id="element-431">
                  <div className="flex items-start gap-3 mb-3" data-id="element-432">
                    <div className="bg-[#D4AF37]/10 p-2 rounded-lg shrink-0" data-id="element-433">
                      <Store className="h-5 w-5 text-[#D4AF37]" data-id="element-434" />
                    </div>
                    <div className="flex-1" data-id="element-435">
                      <h4 className="font-serif text-lg font-bold text-[#2C2C2C] mb-1" data-id="element-436">
                        {market.name}
                      </h4>
                      <div className="flex items-center gap-2 text-xs text-[#5C5C5C] mb-2" data-id="element-437">
                        <span className="flex items-center gap-1" data-id="element-438">
                          <MapPin className="h-3 w-3" data-id="element-439" />
                          {market.distance}
                        </span>
                        <span data-id="element-440">•</span>
                        <span className="flex items-center gap-1" data-id="element-441">
                          <Clock className="h-3 w-3" data-id="element-442" />
                          {market.driveTime}
                        </span>
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-[#5C5C5C] leading-relaxed" data-id="element-443">
                    {market.description}
                  </p>
                </Card>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}